import { useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { LogIn, UserPlus, Mail, Eye, EyeOff } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { fadeInUp, transition } from "@/utils/motion";
import VerificationSentView from "@/components/auth/VerificationSentView";
import UnverifiedView from "@/components/auth/UnverifiedView";
import BlockedView from "@/components/auth/BlockedView";
import ForgotPasswordForm from "@/components/auth/ForgotPasswordForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Login = () => {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [callsign, setCallsign] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(
    searchParams.get("error") === "auth_failed" ? "Google sign-in failed. Please try again." : "",
  );
  const [blockedInfo, setBlockedInfo] = useState(null);
  const [verificationSent, setVerificationSent] = useState(null);
  const [unverifiedEmail, setUnverifiedEmail] = useState(null);
  const [resending, setResending] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);

  if (user) return <Navigate to="/practice" replace />;

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/practice";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleResendVerification = async (targetEmail) => {
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: targetEmail });
      toast.success("Verification email sent!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unable to send");
    } finally {
      setResending(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBlockedInfo(null);
    setUnverifiedEmail(null);
    setLoading(true);
    try {
      if (mode === "register") {
        const res = await axios.post(`${API}/auth/register`, { email, password, name, callsign: callsign.trim() });
        setVerificationSent(res.data.email || email);
      } else {
        const res = await axios.post(`${API}/auth/user-login`, { email, password });
        login(res.data.user);
        toast.success("Welcome back, Helldiver!");
        navigate("/practice", { replace: true });
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403 && typeof detail === "object") {
        if (detail.status === "blocked") setBlockedInfo(detail);
        else if (detail.status === "unverified") setUnverifiedEmail(detail.email);
      } else if (err.response?.status === 403) {
        setError(typeof detail === "string" ? detail : "Account suspended.");
      } else {
        setError(typeof detail === "string" ? detail : "Authentication failed");
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Special state views ──
  if (verificationSent) {
    return (
      <VerificationSentView
        email={verificationSent}
        resending={resending}
        onResend={handleResendVerification}
        onBack={() => { setVerificationSent(null); setMode("login"); }}
      />
    );
  }

  if (unverifiedEmail) {
    return (
      <UnverifiedView
        email={unverifiedEmail}
        resending={resending}
        onResend={handleResendVerification}
        onBack={() => { setUnverifiedEmail(null); setError(""); }}
      />
    );
  }

  if (blockedInfo) {
    return (
      <BlockedView
        blockedInfo={blockedInfo}
        email={email}
        onBack={() => { setBlockedInfo(null); setError(""); }}
      />
    );
  }

  // ── Main login / register form ──
  return (
    <div className="max-w-md mx-auto px-6 py-16" data-testid="login-page">
      <motion.div {...fadeInUp} transition={transition} className="hud-card clip-corners p-8">
        <h1 className="font-headings text-3xl uppercase tracking-widest text-[#FFE800] mb-2" data-testid="login-title">
          Helldiver Login
        </h1>
        <p className="text-sm text-gray-400 mb-8">Sign in to track your progress and compete on the leaderboard.</p>

        <button
          onClick={handleGoogleLogin}
          className="w-full flex items-center justify-center gap-3 bg-white text-black font-headings uppercase tracking-widest py-3 px-4 hover:bg-gray-100 mb-6"
          data-testid="google-login-button"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
          </svg>
          Continue with Google
        </button>

        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-xs text-gray-500 uppercase tracking-widest">or</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        <div className="flex gap-2 mb-6">
          <button
            onClick={() => { setMode("login"); setError(""); }}
            className={`flex-1 py-2 text-xs font-headings uppercase tracking-widest border ${mode === "login" ? "border-[#FFE800] text-[#FFE800]" : "border-white/10 text-gray-400"}`}
            data-testid="login-tab"
          >
            <LogIn className="inline h-3 w-3 mr-1" /> Sign In
          </button>
          <button
            onClick={() => { setMode("register"); setError(""); }}
            className={`flex-1 py-2 text-xs font-headings uppercase tracking-widest border ${mode === "register" ? "border-[#FFE800] text-[#FFE800]" : "border-white/10 text-gray-400"}`}
            data-testid="register-tab"
          >
            <UserPlus className="inline h-3 w-3 mr-1" /> Register
          </button>
        </div>

        <form onSubmit={handleEmailSubmit} className="space-y-4">
          {mode === "register" && (
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Display name"
              className="w-full bg-black/50 border border-white/20 text-white font-data text-sm py-3 px-4 focus:border-[#FFE800] outline-none"
              data-testid="register-name" required />
          )}
          {mode === "register" && (
            <div>
              <input type="text" value={callsign} onChange={(e) => setCallsign(e.target.value)} placeholder="Callsign"
                className="w-full bg-black/50 border border-white/20 text-white font-data text-sm py-3 px-4 focus:border-[#FFE800] outline-none"
                data-testid="register-callsign" required minLength={3} maxLength={20} />
              <p className="mt-1 text-xs text-gray-500">3-20 chars, letters/numbers/hyphens/underscores</p>
            </div>
          )}
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email"
              className="w-full bg-black/50 border border-white/20 text-white font-data text-sm py-3 pl-10 pr-4 focus:border-[#FFE800] outline-none"
              data-testid="login-email" required />
          </div>
          <div className="relative">
            <input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder={mode === "register" ? "Password (min 8 characters)" : "Password"}
              className="w-full bg-black/50 border border-white/20 text-white font-data text-sm py-3 px-4 pr-10 focus:border-[#FFE800] outline-none"
              data-testid="login-password" required minLength={mode === "register" ? 8 : undefined} />
            <button type="button" onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#FFE800]" data-testid="toggle-password">
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>

          {error && <p className="text-sm text-red-400" data-testid="login-error">{error}</p>}

          <button type="submit" disabled={loading}
            className="w-full bg-[#FFE800] text-black font-headings uppercase tracking-widest py-3 hover:bg-[#FFD700] disabled:opacity-40"
            data-testid="login-submit">
            {loading ? "Processing..." : mode === "register" ? "Create Account" : "Sign In"}
          </button>

          {mode === "login" && (
            <button type="button" onClick={() => { setForgotMode(true); }}
              className="w-full text-center mt-3 text-xs text-gray-500 hover:text-[#FFE800]" data-testid="forgot-password-link">
              Forgot your password?
            </button>
          )}
        </form>

        {forgotMode && mode === "login" && <ForgotPasswordForm initialEmail={email} onClose={() => setForgotMode(false)} />}
      </motion.div>
    </div>
  );
};

export default Login;
